import { useMemo } from 'react';
import { useStore } from '@tasca/client-sdk';
import type { Member, Role } from '@tasca/client-sdk';
import { hueOf, initials } from '../lib/avatar.js';
import { Icon } from './Icon.js';

/**
 * Quem está no servidor, à direita da conversa.
 *
 * Os membros já vinham a caminho — o Chat pede-os por REQUEST_MEMBERS ao
 * entrar no guild e o store aplica os GUILD_MEMBER_* a seguir. O que faltava
 * era mostrá-los.
 */

/** Nome que esta pessoa usa AQUI: o nick do guild ganha ao nome global. */
function nameOf(m: Member): string {
  return m.nick ?? m.user.displayName;
}

/**
 * A cor vem do grupo mais alto que tenha uma — e não do mais alto, ponto.
 * Um grupo sem cor (`0`) não deve apagar a cor de um abaixo dele, senão dar a
 * alguém um grupo de organização apagava-lhe a cor que tinha.
 */
function colorOf(m: Member, byId: Map<string, Role>): string | undefined {
  let best: Role | undefined;
  for (const id of m.roles) {
    const role = byId.get(id);
    if (!role || role.color === 0) continue;
    if (!best || role.position > best.position) best = role;
  }
  return best ? `#${best.color.toString(16).padStart(6, '0')}` : undefined;
}

type Group = {
  key: string;
  name: string;
  members: Member[];
};

/**
 * Agrupa por grupo destacado (`hoist`), o resto cai em "Membros".
 *
 * `hoist` existe exactamente para isto: é o grupo a dizer "mostra-me em
 * separado". Quem tem vários fica debaixo do mais alto deles, para aparecer
 * uma vez só.
 */
function group(members: Member[], roles: Role[]): Group[] {
  const hoisted = roles.filter((r) => r.hoist);
  const byId = new Map(roles.map((r) => [r.id, r]));
  const groups: Group[] = hoisted.map((r) => ({ key: r.id, name: r.name, members: [] }));
  const rest: Group = { key: 'rest', name: 'Membros', members: [] };

  for (const m of members) {
    let top: Role | undefined;
    for (const id of m.roles) {
      const role = byId.get(id);
      if (!role?.hoist) continue;
      if (!top || role.position > top.position) top = role;
    }
    const topId = top?.id;
    const target = topId ? groups.find((g) => g.key === topId) : undefined;
    (target ?? rest).members.push(m);
  }

  const byName = (a: Member, b: Member) => nameOf(a).localeCompare(nameOf(b), 'pt-PT');
  for (const g of groups) g.members.sort(byName);
  rest.members.sort(byName);

  return [...groups.filter((g) => g.members.length > 0), rest].filter(
    (g) => g.members.length > 0,
  );
}

export function MemberList({
  onUserMenu,
}: {
  /** Botão direito num membro — as acções vivem no ecrã que nos monta. */
  onUserMenu?: (event: React.MouseEvent, userId: string, name: string) => void;
}): React.ReactElement {
  const activeGuildId = useStore((s) => s.activeGuildId);
  const members = useStore((s) => (activeGuildId ? s.members.get(activeGuildId) : undefined));
  const roles = useStore((s) => (activeGuildId ? s.roles.get(activeGuildId) : undefined));
  const voiceStates = useStore((s) => s.voiceStates);

  const list = useMemo(() => members ?? [], [members]);
  const byId = useMemo(() => new Map((roles ?? []).map((r) => [r.id, r])), [roles]);
  const groups = useMemo(() => group(list, roles ?? []), [list, roles]);

  return (
    <aside className="members" aria-label="Membros">
      <header className="members-head">
        <span>Membros</span>
        <span className="members-total">{list.length}</span>
      </header>

      {list.length === 0 && <p className="hint members-empty">Ainda sem lista de membros.</p>}

      <div className="members-list">
        {groups.map((g) => (
          <section key={g.key}>
            <h3 className="members-group">
              {g.name} — {g.members.length}
            </h3>
            <ul>
              {g.members.map((m) => {
                // Quem está em voz neste momento, em qualquer canal do guild.
                const inVoice = voiceStates.get(m.userId)?.channelId != null;
                return (
                  <li
                    key={m.userId}
                    className="member"
                    onContextMenu={(e) => onUserMenu?.(e, m.userId, nameOf(m))}
                  >
                    <span className="avatar" style={{ ['--hue' as string]: hueOf(m.userId) }}>
                      {initials(nameOf(m))}
                    </span>
                    <span className="member-name" style={{ color: colorOf(m, byId) }}>
                      {nameOf(m)}
                    </span>
                    {m.user.bot && <span className="member-tag">BOT</span>}
                    {inVoice && (
                      <span className="member-voice" title="Em voz">
                        <Icon name="volume" />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </aside>
  );
}
